import { Component, OnInit, inject } from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {DataService} from "../../services/data.service";
import {AccessService} from "../../services/access.service";
import {ToastrService} from "ngx-toastr";
import Swal from "sweetalert2";
import {ViewMode} from "../../interfaces";
import {NgIf} from "@angular/common";

@Component({
    selector: 'app-franchisor-detail',
    imports: [NgIf, ReactiveFormsModule],
    template: `
        <div class="card">
            <div class="card-body">
                <div class="mb-4">
                    <h3>
                        <i class="fa-solid fa-building me-2"></i>
                        @switch (viewMode) {
                            @case (ViewMode.INSERT) {
                                Franchisegeber anlegen
                            }
                            @case (ViewMode.EDIT) {
                                Franchisegeber bearbeiten
                            }
                            @case (ViewMode.VIEW) {
                                Franchisegeber
                            }
                        }
                    </h3>
                </div>

                <form [formGroup]="form">
                    <!-- Allgemein -->
                    <h5 class="mt-2">Allgemein</h5>
                    <div class="row g-3 align-items-end mb-3">
                        <div class="col-6 col-lg-4">
                            <label>Name</label>
                            <input type="text" class="form-control" formControlName="name" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW" class="mb-0">{{ form.value.name }}</p>
                        </div>
                    </div>

                    <!-- Adresse -->
                    <h5>Adresse</h5>
                    <div class="row g-3 align-items-end mb-3">
                        <div class="col-12 col-lg-4">
                            <label>Land</label>
                            <select class="form-control" formControlName="id_country" *ngIf="viewMode != ViewMode.VIEW">
                                <option value="" disabled selected style="display: none;">Bitte auswählen</option>
                                @for (country of countries; track country.id) {
                                    <option [ngValue]="country.id">
                                        {{ country.name }}
                                    </option>
                                }
                            </select>
                            <p *ngIf="viewMode == ViewMode.VIEW" class="mb-0">{{ form.value.country }}</p>
                        </div>
                        <div class="col-6 col-lg-2">
                            <label>PLZ</label>
                            <input type="text" class="form-control" formControlName="zip" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW" class="mb-0">{{ form.value.zip }}</p>
                        </div>
                        <div class="col-6 col-lg-3">
                            <label>Ort</label>
                            <input type="text" class="form-control" formControlName="city" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW" class="mb-0">{{ form.value.city }}</p>
                        </div>
                        <div class="col-12 col-lg-3">
                            <label>Straße</label>
                            <input type="text" class="form-control" formControlName="street" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW" class="mb-0">{{ form.value.street }}</p>
                        </div>
                    </div>

                    <!-- Rechtliches -->
                    <h5>Rechtliches</h5>
                    <div class="row g-3 align-items-end mb-3">
                        <div class="col-12 col-lg-4">
                            <label>Firmenbuchnummer</label>
                            <input type="text" class="form-control" formControlName="register_number" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW" class="mb-0">{{ form.value.register_number }}</p>
                        </div>
                        <div class="col-12 col-lg-4">
                            <label>UID</label>
                            <input type="text" class="form-control" formControlName="vat_number" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW" class="mb-0">{{ form.value.vat_number }}</p>
                        </div>
                        <div class="col-12 col-lg-4">
                            <label>Handelsgericht</label>
                            <input type="text" class="form-control" formControlName="commercial_court" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW" class="mb-0">{{ form.value.commercial_court }}</p>
                        </div>
                    </div>
                </form>

                <div class="mt-3">
                    @if (viewMode == ViewMode.VIEW) {
                        <button type="button" class="btn btn-primary me-2" (click)="setMode(ViewMode.EDIT)">Bearbeiten</button>
                        <button type="button" class="btn btn-secondary" (click)="cancel()">Zurück</button>
                        <button type="button" class="btn btn-danger float-end" (click)="delete()" [disabled]="franchisor?.stores != '0' && franchisor?.franchisees != '0'">Löschen</button>
                    } @else if (viewMode == ViewMode.EDIT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="update()">Aktualisieren</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancelEdit()">Abbrechen</button>
                    } @else if (viewMode == ViewMode.INSERT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="save()">Speichern</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancel()">Abbrechen</button>
                    }
                </div>

            </div>
        </div>
    `
})
export class FranchisorDetailComponent implements OnInit {

    accessService = inject(AccessService);
    private router = inject(Router);
    private fb = inject(UntypedFormBuilder);
    private dataService = inject(DataService);
    private route = inject(ActivatedRoute);
    private toastrService = inject(ToastrService);

    ViewMode = ViewMode;

    form!: UntypedFormGroup;
    id_franchisor!: string;
    viewMode!: ViewMode;
    franchisor: any;

    countries!: any[];

    ngOnInit(): void {
        this.id_franchisor = this.route.snapshot.paramMap.get('id') ?? '0';
        this.viewMode = this.id_franchisor == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            id_country: [null, Validators.required],
            name: [null, Validators.required],
            zip: [null, Validators.required],
            city: [null, Validators.required],
            street: [null, Validators.required],
            register_number: [null, Validators.required],
            vat_number: [null, Validators.required],
            commercial_court: [null, Validators.required],

            country: [null],
        });

        if (this.id_franchisor !== '0') {
            this.getFranchisor();
        }

        this.dataService.request("Country/listForSelection").subscribe(response => {
            this.countries = response.countries;
        });
    }

    getFranchisor(): void {
        this.dataService.request('Franchisor/get', {
            id_franchisor: this.id_franchisor
        }).subscribe(response => {
            this.franchisor = response.franchisor;

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.franchisor.id,
            id_country: this.franchisor.id_country,
            name: this.franchisor.name,
            zip: this.franchisor.zip,
            city: this.franchisor.city,
            street: this.franchisor.street,
            register_number: this.franchisor.register_number,
            vat_number: this.franchisor.vat_number,
            commercial_court: this.franchisor.commercial_court,

            country: this.franchisor.country,
        });
    }

    save(): void {
        this.dataService.request('Franchisor/save', {
            franchisor: this.form.getRawValue(),
        }).subscribe(() => {
            this.router.navigate(['/franchisor']);
            this.toastrService.success('Der Franchisegeber wurde angelegt');
        });
    }

    update(): void {
        this.dataService.request('Franchisor/update', {
            franchisor: this.form.getRawValue(),
        }).subscribe(() => {
            this.setMode(ViewMode.VIEW);
            this.getFranchisor();
            this.toastrService.success('Der Franchisegeber wurde aktualisiert');
        });
    }

    cancel(): void {
        this.router.navigate(['/franchisor']);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
        this.getFranchisor();
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Der Franchisegeber wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Franchisor/delete', {
                    id_franchisor: this.id_franchisor
                }).subscribe(() => {
                    this.cancel();
                    this.toastrService.success('Der Franchisegeber wurde gelöscht');
                });
            }
        });
    }

}
